import { REPORTING_TIMEZONE, formatPeriod, searchTypeLabel } from '../GscTools/period';
import { reportsPosition } from '../GscTools/metrics';
import { VIEW, ruleLabel } from './scope';

/**
 * The scope every export carries in its head, so a file that outlives this page still says what
 * it is: which property, which surface, which two windows, every filter that shaped the request,
 * the reporting timezone and when it was fetched.
 *
 * The session rules are listed one per line exactly as they were sent. A rule left out of the
 * header would make the file look like a wider slice of the property than it is.
 */
function scopeRows({ t, scope, fetchedAt, retrieval }) {
    const rows = [
        [t('tools.gsc.exports.property'), scope.siteUrl],
        [
            t('tools.gsc.exports.source'),
            t('tools.gsc.coverage.source', { type: searchTypeLabel(scope.searchType, t) }),
        ],
        [t('tools.gsc.controls.search_type'), scope.searchType],
        [
            t('tools.pages_queries_changes.controls.view'),
            t(`tools.pages_queries_changes.views.${scope.view}`),
        ],
        [
            t('tools.pages_queries_changes.controls.mode'),
            `${t(`tools.pages_queries_changes.modes.${scope.mode}`)} · ${t(
                `tools.gsc.metrics.${scope.metric}`,
            )}`,
        ],
        [
            t('tools.pages_queries_changes.controls.preset'),
            scope.presetApplies
                ? t(`tools.pages_queries_changes.presets.${scope.preset}`)
                : t('tools.pages_queries_changes.applied.preset_none'),
        ],
        [t('tools.gsc.controls.country'), scope.country || 'all'],
        [t('tools.gsc.controls.device'), scope.device || 'all'],
        [t('tools.gsc.exports.data_state'), t(`tools.gsc.data_state.${scope.dataState}`)],
        [t('tools.gsc.coverage.current'), formatPeriod(scope.current)],
        [t('tools.gsc.coverage.baseline'), formatPeriod(scope.baseline)],
        [t('tools.gsc.exports.timezone'), REPORTING_TIMEZONE],
        [t('tools.gsc.exports.fetched_at'), fetchedAt ?? ''],
        [
            t('tools.pages_queries_changes.retrieval.retrieved_rows'),
            retrieval?.currentRows ?? '',
            retrieval?.priorRows ?? '',
        ],
        [
            t('tools.pages_queries_changes.retrieval.completeness'),
            t('tools.pages_queries_changes.retrieval.not_guaranteed'),
        ],
    ];

    if (!scope.rules.length) {
        rows.push([
            t('tools.pages_queries_changes.rules.title'),
            t('tools.pages_queries_changes.applied.no_rules'),
        ]);
    } else {
        for (const rule of scope.rules) {
            rows.push([t('tools.pages_queries_changes.rules.title'), ruleLabel(rule)]);
        }
    }

    rows.push([]);
    return rows;
}

/**
 * Every retrieved row, with both periods side by side and the reason any cell is empty.
 *
 * Nothing absent is filled in: a period that did not return a row leaves empty metric cells and
 * says so in its own state column, so whoever opens the file cannot sum a zero that Search
 * Console never sent. Values are the raw API ones — CTR as a fraction, position as a mean —
 * because a rounded export cannot be recomputed from.
 */
export function buildRowsCsv({ t, scope, rows, fetchedAt, retrieval }) {
    const out = scopeRows({ t, scope, fetchedAt, retrieval });
    const hasPosition = reportsPosition(scope.searchType);

    const keyLabel =
        scope.view === VIEW.PAGES ? t('tools.gsc.columns.page') : t('tools.gsc.columns.query');

    out.push([
        keyLabel,
        `current ${t('tools.gsc.metrics.clicks_short')}`,
        `current ${t('tools.gsc.metrics.impressions_short')}`,
        `current ${t('tools.gsc.metrics.ctr_short')}`,
        ...(hasPosition ? [`current ${t('tools.gsc.metrics.position_short')}`] : []),
        t('tools.gsc.exports.state'),
        `prior ${t('tools.gsc.metrics.clicks_short')}`,
        `prior ${t('tools.gsc.metrics.impressions_short')}`,
        `prior ${t('tools.gsc.metrics.ctr_short')}`,
        ...(hasPosition ? [`prior ${t('tools.gsc.metrics.position_short')}`] : []),
        t('tools.gsc.exports.state'),
        t('tools.gsc.columns.delta_clicks'),
        t('tools.gsc.columns.delta_percent'),
        t('tools.gsc.exports.reason'),
    ]);

    const returned = t('tools.gsc.exports.returned');
    const notReturned = t('tools.gsc.not_returned');

    for (const row of rows) {
        out.push([
            row.key,
            row.current?.clicks ?? null,
            row.current?.impressions ?? null,
            row.current?.ctr ?? null,
            ...(hasPosition ? [row.current?.position ?? null] : []),
            row.current ? returned : notReturned,
            row.prior?.clicks ?? null,
            row.prior?.impressions ?? null,
            row.prior?.ctr ?? null,
            ...(hasPosition ? [row.prior?.position ?? null] : []),
            row.prior ? returned : notReturned,
            row.deltaClicks ?? null,
            row.deltaPercent ?? null,
            row.comparable
                ? row.prior.clicks === 0
                    ? t('tools.gsc.no_baseline_row')
                    : null
                : t('tools.gsc.not_comparable'),
        ]);
    }

    return out;
}
